import type { MyServiceTask } from '@/hooks/useWorkOrders';

/**
 * A work order and all of the viewed mechanic's services in it, kept together
 * so that starting one service never hides its sibling services (issue #428).
 */
export interface ServiceWoGroup {
  type: 'woGroup';
  workOrderId: string;
  services: MyServiceTask[];
}

export interface ServiceSections {
  /** Work orders with at least one active (in-progress/paused) service. */
  ongoing: ServiceWoGroup[];
  /** Work orders with no active service and at least one not-yet-completed service. */
  scheduled: ServiceWoGroup[];
  /** Work orders whose services are all completed. */
  completed: ServiceWoGroup[];
}

function isActive(status: MyServiceTask['status']): boolean {
  return status === 'in_progress' || status === 'paused';
}

/**
 * Group the viewed mechanic's service tasks into ongoing / scheduled / completed
 * sections, grouping every work order EXACTLY ONCE so all of its services stay
 * in a single card regardless of their individual status.
 *
 * Previously the page grouped services per-section (ongoing vs scheduled)
 * separately, which split one work order across two cards the moment a single
 * service was started — making the still-pending sibling services "disappear"
 * from the card the mechanic was working in (issue #428).
 *
 * The three inputs are the page's three service queries. They can overlap (the
 * day query and the status-scoped ongoing/paused queries), so services are
 * de-duplicated by `bookingServiceId`; the day-query entry wins because it
 * carries the current status for the selected day.
 *
 * A work order is "ongoing" only when the selected day is today, matching the
 * existing rule that active work is surfaced on the current day.
 */
export function buildServiceSections(
  dayServices: MyServiceTask[],
  ongoingServices: MyServiceTask[],
  pausedServices: MyServiceTask[],
  isSelectedToday: boolean,
): ServiceSections {
  // De-duplicate by bookingServiceId. Day-query entries are appended last so
  // they overwrite the status-scoped snapshots with the current-day status.
  const byId = new Map<string, MyServiceTask>();
  for (const svc of [...ongoingServices, ...pausedServices, ...dayServices]) {
    byId.set(svc.bookingServiceId, svc);
  }

  // Group by work order, preserving first-seen order.
  const groups = new Map<string, MyServiceTask[]>();
  for (const svc of byId.values()) {
    const existing = groups.get(svc.workOrderId);
    if (existing) {
      existing.push(svc);
    } else {
      groups.set(svc.workOrderId, [svc]);
    }
  }

  const ongoing: ServiceWoGroup[] = [];
  const scheduled: ServiceWoGroup[] = [];
  const completed: ServiceWoGroup[] = [];

  for (const [workOrderId, services] of groups) {
    const group: ServiceWoGroup = { type: 'woGroup', workOrderId, services };
    if (isSelectedToday && services.some((s) => isActive(s.status))) {
      ongoing.push(group);
    } else if (services.every((s) => s.status === 'completed')) {
      completed.push(group);
    } else {
      scheduled.push(group);
    }
  }

  return { ongoing, scheduled, completed };
}
